import Box from '@mui/material/Box';
import MuiDrawer, { DrawerProps } from '@mui/material/Drawer';
import { Breakpoint, styled } from '@mui/material/styles';
import { useCallback, useImperativeHandle, useState } from 'react';

export type ICallableRef = {
    setOpen: (open: boolean) => void;
};
export type IDrawerContainerProps = Omit<DrawerProps, 'onClose'> & {
    innerRef?: React.Ref<ICallableRef>;
    name?: string;
    maxWidth?: string;
    size?: Breakpoint;
    onClose?: () => void;
};

const StyledContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'size'
})<{ size?: Breakpoint }>(({ theme, size = 'md' }) => {
    const sizes: { [key: string]: string } = {
        xs: '20vw',
        sm: '25vw',
        md: '35vw',
        lg: '45vw',
        xl: '50vw'
    };

    return `
        width: ${sizes[size] || sizes.md};
        max-width: 1000px;
        height: 100%;
        padding: ${theme.spacing(3)} ${theme.spacing(5)};
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(3)};
    `;
});

export const DrawerContainer = ({
    anchor = 'right',
    name,
    innerRef,
    size = 'md',
    open = false,
    onClose,
    ...props
}: IDrawerContainerProps) => {
    const [isOpen, setOpen] = useState(open);

    const handleClose = useCallback(() => {
        setOpen(false);
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    useImperativeHandle(innerRef, () => ({
        setOpen: (newState) => {
            if (newState !== isOpen) {
                if (newState) {
                    setOpen(newState);
                } else {
                    handleClose();
                }
            }
        }
    }));

    return (
        <MuiDrawer {...props} anchor={anchor} open={isOpen} onClose={handleClose}>
            <StyledContainer className="muiHookForm" size={size} data-testid={`drawer-${name}`}>
                {props.children}
            </StyledContainer>
        </MuiDrawer>
    );
};
